import { useState, useCallback } from 'react';
import { Download, FileImage, Map, Layers, File, Pencil } from 'lucide-react';
import { artifactExportService, ExportFormat } from '../../services/artifactExportService';
import { useArtifactActions } from '../../contexts/ArtifactActionsContext';
import { useUserProjectsContext } from '../../contexts/UserProjectsContext';
export const useContextMenu = (options = {}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [position, setPosition] = useState({ x: 0, y: 0 });
    const [menuItems, setMenuItems] = useState([]);
    const { onExportArtifact, onGetSupportedFormats, isArtifactExporting } = useArtifactActions();
    // Show context menu with items
    const showMenu = useCallback((newPosition, items) => {
        setPosition(newPosition);
        setMenuItems(items);
        setIsOpen(true);
    }, []);
    // Hide context menu
    const hideMenu = useCallback(() => {
        setIsOpen(false);
        setMenuItems([]);
    }, []);
    return {
        isOpen,
        position,
        menuItems,
        showMenu,
        hideMenu
    };
};
// Helper function to generate export menu items for an artifact
export const useExportMenuItems = () => {
    const { onExportArtifact, onGetSupportedFormats, isArtifactExporting } = useArtifactActions();
    // The shared context, not a second useUserProjects instance: calling the hook
    // directly stands up a whole parallel copy — its own polling loop and its own
    // event subscriptions — for what is only a "does this user have any projects"
    // check below.
    const { projects } = useUserProjectsContext();
    const generateExportMenuItems = useCallback(async (artifactId, metadata, isDemoProject, onShowDemoRestriction, onShowAccountNudge, isAuthenticated, onShowProjectCreationNudge, isUserProjectWithDemoStatus, onShowPurchasePrompt) => {
        try {
            // Get supported formats for this artifact
            const allFormats = await onGetSupportedFormats(artifactId, isDemoProject, metadata);
            // Filter out UNSPECIFIED formats and the globe-aligned PNG, which is
            // a niche reprojected output (warped for use on a 3D sphere) that
            // most users don't want — having both "PNG" and a "globe PNG" in
            // the menu forces a vocabulary decision the user can't make. Flat
            // PNG (EXPORT_FORMAT_PNG_MAP, surfaced just as "PNG") + GeoTIFF
            // cover the real use cases.
            const availableFormats = allFormats.filter(format => format.format !== ExportFormat.EXPORT_FORMAT_UNSPECIFIED &&
                format.format !== undefined &&
                format.format !== ExportFormat.EXPORT_FORMAT_PNG);
            if (availableFormats.length === 0) {
                return [{
                        id: `no-exports-${artifactId}`,
                        label: 'No export formats available',
                        icon: File,
                        action: () => {
                        },
                        disabled: true
                    }];
            }
            // Generate menu items for each supported format
            const exportItems = availableFormats.map((availableFormat, index) => {
                const formatName = artifactExportService.getFormatName(availableFormat.format);
                const isExporting = isArtifactExporting(availableFormat.artifactId);
                // Debug logging for format issues
                if (availableFormat.format === ExportFormat.EXPORT_FORMAT_UNSPECIFIED || availableFormat.format === undefined) {
                    console.warn('⚠️ Received UNSPECIFIED or undefined format:', {
                        format: availableFormat.format,
                        artifactId: availableFormat.artifactId,
                        formatName
                    });
                }
                return {
                    id: `export-${availableFormat.artifactId}-${availableFormat.format}-${index}`,
                    label: `Export as ${formatName}`,
                    icon: getFormatIcon(availableFormat.format),
                    action: async () => {
                        // Check if user project has demo status - show purchase prompt
                        if (isUserProjectWithDemoStatus && onShowPurchasePrompt) {
                            onShowPurchasePrompt();
                            return;
                        }
                        // Check authentication upfront for demo projects (anonymous users only)
                        if (isDemoProject && !isAuthenticated && onShowAccountNudge) {
                            // Anonymous user on demo project - show nudge immediately
                            onShowAccountNudge({
                                action: 'export',
                                message: 'Sign up for a free account to download map data and layers'
                            });
                            return;
                        }
                        // Check if authenticated user has no projects (project creation nudge) - for demo projects
                        if (isAuthenticated && isDemoProject && projects.length === 0 && onShowProjectCreationNudge) {
                            // Authenticated user with no projects downloading demo artifact - show project creation nudge
                            onShowProjectCreationNudge({
                                action: 'export',
                                onProceedWithDownload: async () => {
                                    try {
                                        await onExportArtifact(availableFormat.artifactId, availableFormat.format, isDemoProject);
                                    }
                                    catch (error) {
                                        console.error('Export failed after proceeding from project creation nudge:', error);
                                    }
                                }
                            });
                            return;
                        }
                        try {
                            // Proceed with export (authenticated user or non-demo project)
                            await onExportArtifact(availableFormat.artifactId, availableFormat.format, isDemoProject);
                        }
                        catch (error) {
                            // Handle any remaining errors (shouldn't get auth errors for authenticated users)
                            if (error && error.code === 7 && isDemoProject && onShowDemoRestriction) { // 7 is grpc.Code.PERMISSION_DENIED
                                onShowDemoRestriction({ action: 'export' });
                                return;
                            }
                            // For authentication errors, only show nudge if user is actually not authenticated
                            if (error && error.code === 16 && !isAuthenticated && onShowAccountNudge) { // 16 is grpc.Code.UNAUTHENTICATED
                                onShowAccountNudge({
                                    action: 'export',
                                    message: error.message || 'Sign up for a free account to download map data and layers'
                                });
                                return;
                            }
                            // If authenticated user gets auth error, log it but don't show nudge
                            if (error && error.code === 16 && isAuthenticated) {
                                console.error('Authenticated user received authentication error - this may indicate a session issue:', error);
                            }
                            // Re-throw other errors to be handled by normal error handling
                            throw error;
                        }
                    },
                    disabled: isExporting,
                    loading: isExporting
                };
            });
            // Add separator and header
            return [
                {
                    id: `export-header-${artifactId}`,
                    label: `Export "${metadata.getName()}"`,
                    icon: Download,
                    action: () => { },
                    disabled: true,
                    separator: false
                },
                ...exportItems
            ];
        }
        catch (error) {
            console.error('Error generating export menu items:', error);
            return [{
                    id: `export-error-${artifactId}`,
                    label: 'Export options unavailable',
                    icon: File,
                    action: () => { },
                    disabled: true
                }];
        }
    }, [onExportArtifact, onGetSupportedFormats, isArtifactExporting, projects]);
    return { generateExportMenuItems };
};
// Get icon for export format
function getFormatIcon(format) {
    switch (format) {
        case ExportFormat.EXPORT_FORMAT_UNSPECIFIED:
            return File;
        case ExportFormat.EXPORT_FORMAT_TIF:
        case ExportFormat.EXPORT_FORMAT_PNG:
        case ExportFormat.EXPORT_FORMAT_PNG_MAP:
            return FileImage;
        case ExportFormat.EXPORT_FORMAT_KML:
        case ExportFormat.EXPORT_FORMAT_GEOJSON:
        case ExportFormat.EXPORT_FORMAT_SHAPEFILE:
            return Map;
        case ExportFormat.EXPORT_FORMAT_SVG:
            return Pencil;
        case ExportFormat.EXPORT_FORMAT_DXF:
            return Layers;
        default:
            return File;
    }
}
